// ----------------------------------------------------------------------- react
import { useCallback, useRef, useState } from "react";

// ----------------------------------------------------------------------- internos
import { analizarPlanilla, aplicarPlanilla } from "../services/planillaMovimientos";
import { retener, PORQUE_NO_SE_PUDO_LEER } from "../services/archivoRetenido";

/* Subir la planilla de movimientos y cargar los viajes que faltan.
 *
 * ── DE DÓNDE SALIÓ ────────────────────────────────────────────────────────
 *
 * Nico, 30/08: Ricardo se niega a cargar los viajes en el hub y los va a seguir
 * anotando en el Excel; Aldo se compromete a pasarlos al sistema, aunque no es
 * su rol. Esta pantalla existe para que a Aldo le cueste dos clicks en vez de
 * tipear los viajes de vuelta.
 *
 * ── TRES GRUPOS, Y EL TERCERO ES EL QUE IMPORTA ───────────────────────────
 *
 * Lo nuevo viene tildado: es lo que se vino a hacer. Lo que ya está se cuenta
 * y no se lista —son miles de filas y no hay nada que decidir—. Y las
 * correcciones vienen DESTILDADAS, con el antes y el después campo por campo.
 *
 * Que arranquen destildadas es la decisión de toda la pantalla. Una corrección
 * es «hay un viaje del mismo camión, el mismo día, con algo distinto», y eso
 * puede ser dos cosas muy diferentes: que Ricardo arregló un dato, o que el
 * camión salió dos veces ese día. Lo segundo pasa. Tildadas por defecto, el
 * camino cómodo —apretar confirmar— pisaría un viaje real, y eso no se ve
 * después.
 *
 * ── EL ARCHIVO NO SE SUELTA ENTRE UN PASO Y EL OTRO ───────────────────────
 *
 * Se guarda en el estado y se manda de nuevo al confirmar, porque el servidor
 * lo relee: de la pantalla salen decisiones, nunca datos.
 */

const CAMPOS = {
  chofer: "Chofer",
  tractor: "Tractor",
  furgones: "Furgones",
  cliente: "Cliente",
  destino: "Destino",
  origen: "Origen",
  fecha_carga: "Carga",
  fecha_salida: "Salida",
  fecha_llegada: "Llegada",
  sentido: "Sentido",
  empresa: "Empresa",
  obs: "Observaciones",
};

const vacio = (v) => (v === "" || v === null || v === undefined ? "—" : v);

/* Cómo se titula un conflicto. Aldo, 11/09: *«estaría bueno que te ponga desde
 * dónde salió aparte de la fecha de salida»*.
 *
 * Dos tramos del mismo tractor el mismo día se veían iguales en el título, y
 * para saber cuál era cuál había que leer la tabla de campos de cada uno. El
 * origen es justamente lo que los separa, así que va arriba con el resto.
 *
 * Si la planilla no dice de dónde salió, acá dice «desde —». Es a propósito:
 * un origen en blanco escondido se lee igual que uno cargado, y el título es
 * lo único que Aldo mira antes de elegir con cuál se queda.
 */
const tituloDe = (fila) =>
  `tractor ${vacio(fila?.tractor)} del ${vacio(fila?.fecha_salida)}`
  + ` desde ${vacio(fila?.origen)}`;

/* «5 viajes nuevos y 1 corregido.» — en singular cuando es uno.
 *
 * Decía «1 corregidos» y se veía en la pantalla cada vez que Aldo aceptaba una
 * sola corrección, que es el caso normal: las correcciones se miran de a una.
 *
 * Los descartes se cuentan aparte: no cargan nada, así que meterlos en la misma
 * frase que los viajes haría creer que entró algo. Se dicen como lo que son —
 * decisiones que quedan recordadas. */
const resumenDe = (h) => {
  const n = h.insertados;
  const c = h.corregidos;
  const d = h.descartados || 0;
  const uno = `${n} ${n === 1 ? "viaje nuevo" : "viajes nuevos"}`;
  let s = c ? `${uno} y ${c} ${c === 1 ? "corregido" : "corregidos"}.` : `${uno}.`;
  if (d) {
    s += ` Y ${d} ${d === 1 ? "decisión guardada" : "decisiones guardadas"} `
      + `para no volver a ofrecer${d === 1 ? "la" : "las"}.`;
  }
  const r = h.rellenados || 0;
  if (r) {
    s += ` Y se le completó la salida a ${r} `
      + `${r === 1 ? "viaje que estaba" : "viajes que estaban"} sin ella.`;
  }
  return s;
};

const SubirPlanilla = () => {
  const [archivo, setArchivo] = useState(null);
  const [analisis, setAnalisis] = useState(null);
  const [mirando, setMirando] = useState(false);
  const [cargando, setCargando] = useState(false);
  const [error, setError] = useState(null);
  const [hecho, setHecho] = useState(null);
  const [encima, setEncima] = useState(false);
  /* Las decisiones. Lo nuevo arranca todo elegido; las cuasi-duplicadas, sin
   * elegir — hay que decidir una por una (ver el encabezado). `descartes` son
   * las que se decidió DEJAR como están. */
  const [nuevas, setNuevas] = useState(() => new Set());
  const [correcciones, setCorrecciones] = useState(() => new Set());
  const [descartes, setDescartes] = useState(() => new Set());
  /* Días tipeados a mano para las celdas que vinieron con «X». Clave del mapa:
   * `${clave}|${campo}` (campo = carga | salida). */
  const [fechasX, setFechasX] = useState(() => new Map());
  const entrada = useRef(null);

  const limpiar = () => {
    setAnalisis(null);
    setError(null);
    setHecho(null);
    setNuevas(new Set());
    setCorrecciones(new Set());
    setDescartes(new Set());
    setFechasX(new Map());
  };

  const ponerFechaX = (clave, campo, dia) => {
    setFechasX((prev) => {
      const m = new Map(prev);
      const k = `${clave}|${campo}`;
      if (dia === "" || dia == null) m.delete(k);
      else m.set(k, dia);
      return m;
    });
  };

  const analizar = useCallback(async (f) => {
    limpiar();
    setMirando(true);
    /* Los bytes se leen ACÁ y se guarda la copia, no el `File` del disco: al
     * confirmar el archivo se sube de nuevo, y entre un paso y el otro pasan
     * minutos en los que el Excel se abre, se guarda o se mueve. Ver el
     * encabezado de `archivoRetenido` — es el caso de Aldo del 29/09. */
    let copia;
    try {
      copia = await retener(f);
    } catch {
      setMirando(false);
      setArchivo(null);
      setError(PORQUE_NO_SE_PUDO_LEER);
      return;
    }
    setArchivo(copia);
    const r = await analizarPlanilla(copia);
    setMirando(false);
    if (!r.ok) {
      setError(r.mensaje || "No se pudo leer el archivo.");
      return;
    }
    setAnalisis(r);
    setNuevas(new Set((r.nuevos || []).map((n) => n.clave)));
  }, []);

  const elegido = (e) => {
    const f = e.target.files?.[0];
    if (f) analizar(f);
  };

  const soltar = (e) => {
    e.preventDefault();
    setEncima(false);
    const f = e.dataTransfer.files?.[0];
    if (f) analizar(f);
  };

  const alternar = (conjunto, poner, clave) => {
    const s = new Set(conjunto);
    if (s.has(clave)) s.delete(clave);
    else s.add(clave);
    poner(s);
  };

  /* La elección de una cuasi-duplicada. Aldo pidió que sea explícita: en vez de
   * un tilde ambiguo, opciones por viaje.
   *   dejar      → me quedo con el que YA ESTÁ (hub). No carga nada, PERO se
   *                recuerda: la próxima subida no lo vuelve a ofrecer.
   *   reemplazar → traigo el de la PLANILLA encima del que está (corrección).
   *   aparte     → son dos viajes distintos: cargo el de la planilla como nuevo.
   * Arranca SIN elegir (null): dejar como está no puede ser el default, porque
   * entonces una fila que Aldo no miró quedaría decidida sola. Cada clave vive
   * en a lo sumo un conjunto. */
  const eleccionDe = (clave) =>
    correcciones.has(clave)
      ? "reemplazar"
      : nuevas.has(clave)
        ? "aparte"
        : descartes.has(clave)
          ? "dejar"
          : null;

  const elegir = (clave, opcion) => {
    setCorrecciones((prev) => {
      const s = new Set(prev);
      if (opcion === "reemplazar") s.add(clave);
      else s.delete(clave);
      return s;
    });
    setNuevas((prev) => {
      const s = new Set(prev);
      if (opcion === "aparte") s.add(clave);
      else s.delete(clave);
      return s;
    });
    setDescartes((prev) => {
      const s = new Set(prev);
      if (opcion === "dejar") s.add(clave);
      else s.delete(clave);
      return s;
    });
  };

  const confirmar = async () => {
    setCargando(true);
    setError(null);
    const elegidas = (analisis.correcciones || [])
      .filter((c) => correcciones.has(c.clave))
      .map((c) => ({ clave: c.clave, id: c.id }));
    /* Los descartes salen de las dos listas con conflicto —correcciones y
     * finalizados—: en las dos, «dejar el que está» es una decisión que hay que
     * recordar para no re-ofrecerla. Cada una lleva el id del viaje del hub que
     * se decidió conservar. */
    const dejados = [
      ...(analisis.correcciones || []),
      ...(analisis.finalizados || []),
    ]
      .filter((c) => descartes.has(c.clave))
      .map((c) => ({ clave: c.clave, id: c.id }));
    /* Los días tipeados a mano para las celdas con «X». Van como decisiones
     * (clave + campo + día); el servidor los aplica sólo donde relee una «X». */
    const overridesX = (analisis.celdas_x || [])
      .map((x) => {
        const dia = fechasX.get(`${x.clave}|${x.campo}`);
        return dia ? { clave: x.clave, campo: x.campo, dia: Number(dia) } : null;
      })
      .filter(Boolean);
    /* Los rellenos (Igna, 25/09): completar una salida/llegada —y desde el
     * 04/10 también el chofer, el tractor o la empresa de la fila de la mañana
     * que había entrado sin ellos— que estaba en blanco no pisa nada, así que van TODOS y sin tilde —no se eligen—. El
     * servidor relee el valor del archivo y sólo escribe si el campo sigue
     * vacío. Con esto, la re-subida que sólo trae la salida ya no se queda
     * esperando un clic que Aldo no daba. */
    const rellenos = (analisis.rellenos || []).map((r2) => ({
      clave: r2.clave,
      id: r2.id,
    }));
    /* «Es otro viaje — cargarlo aparte» (ticket #403, Aldo 14/10): la clave
     * SOLA no alcanza. El conflicto se ofreció porque la fila comparte con
     * ese viaje la huella con la que FlotaBot evita duplicados, y sin decir
     * «es otro que ÉSTE» el servidor la frenaba por esa misma huella: no
     * entraba nunca. Viaja como las correcciones y los descartes, con el id
     * del viaje del hub. La clave sigue yendo además en `nuevas`, que es como
     * la entiende un FlotaBot anterior a este cambio; el nuevo la carga una
     * sola vez. */
    const apartes = [
      ...(analisis.correcciones || []),
      ...(analisis.finalizados || []),
    ]
      .filter((c) => nuevas.has(c.clave))
      .map((c) => ({ clave: c.clave, id: c.id }));
    const r = await aplicarPlanilla(
      archivo,
      [...nuevas],
      elegidas,
      dejados,
      overridesX,
      rellenos,
      apartes,
    );
    setCargando(false);
    if (!r.ok) {
      setError(r.mensaje || "No se pudo cargar.");
      return;
    }
    setHecho(r);
    setAnalisis(null);
  };

  /* Los rellenos cuentan para habilitar el botón aunque no haya nada tildado:
   * una re-subida que sólo trae salidas en blanco por completar igual tiene
   * trabajo para hacer (se aplican solos). */
  const cuantas = analisis
    ? nuevas.size +
      correcciones.size +
      descartes.size +
      (analisis.rellenos || []).length
    : 0;

  return (
    <div>
      <p className="tk-meta">
        Subí el Excel de <b>Movimientos Diarios</b> y se cargan los viajes que
        todavía no están. Lo que ya está no se toca.
      </p>

      {/* ── EL CAJÓN ────────────────────────────────────────────────────── */}
      {/* Soltar Y buscar en el equipo: quien está acostumbrado a arrastrar lo
          arrastra, y quien no, aprieta. Ofrecer una sola de las dos deja
          afuera a la mitad. */}
      <div
        className={"planilla-cajon" + (encima ? " planilla-cajon--encima" : "")}
        onDragOver={(e) => {
          e.preventDefault();
          setEncima(true);
        }}
        onDragLeave={() => setEncima(false)}
        onDrop={soltar}
      >
        <p className="planilla-cajon__txt">
          Arrastrá la planilla acá
          <br />
          <span className="tk-meta">o</span>
        </p>
        <button
          type="button"
          className="tk-boton"
          onClick={() => entrada.current?.click()}
        >
          Buscar en el equipo
        </button>
        <input
          ref={entrada}
          type="file"
          accept=".xlsx,.xlsm"
          onChange={elegido}
          hidden
          aria-label="Planilla de movimientos"
        />
        {archivo && <p className="tk-meta">{archivo.name}</p>}
      </div>

      {mirando && <p className="tk-meta">Leyendo la planilla…</p>}

      {error && (
        /* `suave` o el mensaje TAPA LA PANTALLA: `panel-aviso` a secas es un
           overlay `absolute; inset: 0`, y como `.hub-main` no es `relative`
           acá, se ancla a la ventana y se come el rail y la cabecera. */
        <div className="panel-aviso suave">
          <p>{error}</p>
        </div>
      )}

      {/* ── LO QUE SE CARGÓ ─────────────────────────────────────────────── */}
      {hecho && (
        /* `suave` por lo mismo que arriba: sin él, el «Listo» se abría encima
           de todo el hub y sólo se salía con el botón de atrás. */
        <div className="panel-aviso suave">
          <h3>Listo</h3>
          <p>{resumenDe(hecho)}</p>
          {/* Lo que falló SE DICE. Un resumen que sólo cuenta lo que salió bien
              deja a Aldo creyendo que entró todo. */}
          {hecho.fallaron?.length > 0 && (
            <p className="rto-tarde">
              No entraron {hecho.fallaron.length}: {hecho.fallaron.join(" · ")}
            </p>
          )}
        </div>
      )}

      {/* ── EL ANÁLISIS ─────────────────────────────────────────────────── */}
      {analisis && (
        <>
          <p className="tk-meta">
            {analisis.leidos} filas leídas ·{" "}
            <b>{(analisis.nuevos || []).length} nuevas</b> ·{" "}
            {(analisis.ya_estan || []).length} ya estaban ·{" "}
            {(analisis.correcciones || []).length} para revisar
            {(analisis.rellenos || []).length > 0 && (
              <>
                {" "}·{" "}
                <b>
                  {(analisis.rellenos || []).length} con datos en blanco para
                  completar
                </b>{" "}
                (chofer, tractor o fechas que faltaban: se cargan solos al
                confirmar)
              </>
            )}
            {(analisis.historicos || 0) > 0 && (
              <>
                {" "}· {analisis.historicos} históricas (antes de agosto), quedan
                como historia
              </>
            )}
            {(analisis.ya_decididas || []).length > 0 && (
              <>
                {" "}· {(analisis.ya_decididas || []).length} ya las decidiste
                antes (no se vuelven a ofrecer)
              </>
            )}
          </p>

          {/* ── FECHAS CON «X» — corregir a mano ─────────────────────────────
              La planilla marca a veces una «X» en la celda de carga (o salida)
              en vez del día. Una «X» no es una fecha: el bot NO la inventa, la
              avisa acá con la celda exacta para que se corrija en la planilla y
              se vuelva a subir. (No se puede tipear la fecha en el hub: al
              confirmar se manda la decisión y el servidor RELEE el Excel, así
              que el valor bueno tiene que estar en el archivo.) */}
          {(analisis.celdas_x || []).length > 0 && (
            <>
              <h3>⚠ Fechas con «X» — poné el día a mano</h3>
              <p className="tk-meta">
                Estas {(analisis.celdas_x || []).length} fila(s) tienen una
                «X» en vez de un día. Escribí acá el día que corresponda (del mes
                de la hoja) y se carga junto con el viaje. Lo que dejes en blanco
                queda sin esa fecha.
              </p>
              <div className="tabla-wrap">
                <table className="tabla fija">
                  <thead>
                    <tr>
                      <th>Celda con X</th>
                      <th>Día (a mano)</th>
                      <th>Hoja</th>
                      <th>Sentido</th>
                      <th>Chofer</th>
                      <th>Tractor</th>
                      <th>La otra fecha</th>
                    </tr>
                  </thead>
                  <tbody>
                    {(analisis.celdas_x || []).map((x, i) => (
                      <tr key={`x-${i}`}>
                        <td className="rto-tarde">
                          <b>{x.campo === "carga" ? "Carga" : "Salida"}</b>
                        </td>
                        <td>
                          <input
                            type="number"
                            min="1"
                            max="31"
                            inputMode="numeric"
                            style={{ width: "4rem" }}
                            value={fechasX.get(`${x.clave}|${x.campo}`) || ""}
                            onChange={(e) =>
                              ponerFechaX(x.clave, x.campo, e.target.value)
                            }
                            aria-label={`Día de ${x.campo} para ${x.chofer || ""}`}
                          />
                        </td>
                        <td>{vacio(x.hoja)}</td>
                        <td>{vacio(x.sentido)}</td>
                        <td>{vacio(x.chofer)}</td>
                        <td>{vacio(x.tractor)}</td>
                        <td>{vacio(x.otra_fecha)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </>
          )}

          {/* Fechas que venían escritas como TEXTO y se leyeron igual: no hay
              nada que corregir, sólo se avisa por si la deducción no es la que
              se esperaba. */}
          {(analisis.fechas_desde_texto || []).length > 0 && (
            <p className="tk-meta">
              Nota: {(analisis.fechas_desde_texto || []).length} fecha(s) venían
              escritas como texto en la planilla; las leí igual. Revisá que la
              interpretación sea correcta.
            </p>
          )}

          {/* VIAJES SIN CHOFER — pedido de Aldo por Igna, 18/09: «cuando un
              viaje viene todo cargado pero no tiene chofer, hacer una marca y
              decirle a Aldo: che, este viaje no tiene chofer, ¿qué hacemos?».

              NO SE FRENAN Y NO SE TILDAN. Un viaje sin chofer es incompleto,
              no inválido: entra igual con el furgón, el cliente y el destino
              que sí trae, y esta lista es para decidir qué se hace después.
              Sacarlo de la carga perdería el resto del dato.

              Los marcados «puede duplicar» van primero porque son los únicos
              que pueden terminar en dos filas: el índice que reconoce la
              re-subida con el chofer ya puesto se ancla en el tractor, y sin
              tractor no reconoce nada. */}
          {(analisis.sin_chofer || []).length > 0 && (
            <>
              <h3>Viajes sin chofer</h3>
              <p className="tk-meta">
                {(analisis.sin_chofer || []).length} viaje(s) vienen cargados
                pero sin chofer. Entran igual — esto es para decidir qué se
                hace con ellos, no para frenarlos.
                {(analisis.sin_chofer || []).some((v) => v.puede_duplicar) && (
                  <>
                    {" "}Los marcados <b>ojo</b> además no tienen tractor: si
                    la planilla se vuelve a subir con el chofer puesto, ese
                    viaje entra de nuevo y quedan dos.
                  </>
                )}
              </p>
              <div className="tabla-wrap">
                <table className="tabla fija">
                  <thead>
                    <tr>
                      <th />
                      <th>Carga</th>
                      <th>Salida</th>
                      <th>Sentido</th>
                      <th>Tractor</th>
                      <th>Furgones</th>
                      <th>Cliente</th>
                      <th>Destino</th>
                    </tr>
                  </thead>
                  <tbody>
                    {[...(analisis.sin_chofer || [])]
                      .sort((a, b) => (b.puede_duplicar ? 1 : 0) - (a.puede_duplicar ? 1 : 0))
                      .map((v, i) => (
                        <tr key={v.clave || i}>
                          <td>
                            {v.puede_duplicar && (
                              <span className="rto-tarde" title="Sin tractor: puede entrar dos veces">
                                ojo
                              </span>
                            )}
                          </td>
                          <td>{vacio(v.fecha_carga)}</td>
                          <td>{vacio(v.fecha_salida)}</td>
                          <td>{v.sentido || "—"}</td>
                          <td>{v.tractor || "—"}</td>
                          <td>{(v.furgones || []).join(", ") || "—"}</td>
                          <td>{v.cliente || "—"}</td>
                          <td>{v.destino || "—"}</td>
                        </tr>
                      ))}
                  </tbody>
                </table>
              </div>
            </>
          )}

          {(analisis.nuevos || []).length > 0 && (
            <>
              <h3>Viajes nuevos</h3>
              <p className="tk-meta">
                Vienen elegidos: es lo que no está en el sistema.
              </p>
              <div className="tabla-wrap">
                <table className="tabla fija">
                  <thead>
                    <tr>
                      <th />
                      <th>Salida</th>
                      <th>Tractor</th>
                      <th>Chofer</th>
                      <th>Cliente</th>
                      <th>Destino</th>
                    </tr>
                  </thead>
                  <tbody>
                    {(analisis.nuevos || []).map((n) => (
                      <tr key={n.clave}>
                        <td>
                          <input
                            type="checkbox"
                            checked={nuevas.has(n.clave)}
                            onChange={() => alternar(nuevas, setNuevas, n.clave)}
                            aria-label={`Cargar ${n.fila?.tractor || ""} ${n.fila?.fecha_salida || ""}`}
                          />
                        </td>
                        <td>{vacio(n.fila?.fecha_salida)}</td>
                        <td>{vacio(n.fila?.tractor)}</td>
                        <td>{vacio(n.fila?.chofer)}</td>
                        <td>{vacio(n.fila?.cliente)}</td>
                        <td>{vacio(n.fila?.destino)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </>
          )}

          {(analisis.correcciones || []).length > 0 && (
            <>
              <h3>Para revisar</h3>
              {/* El texto explica POR QUÉ hay que decidir una por una. Sin eso,
                  la lectura natural es «el sistema no las quiere». */}
              <p className="tk-meta">
                Hay un viaje parecido ya cargado —el mismo chofer, el mismo
                tractor o el mismo furgón, el mismo día y en el mismo sentido—
                con algo distinto. Puede ser que se haya corregido el dato — o
                que sean dos viajes de verdad, que pasa.
                Por eso <b>vos elegís con cuál quedarte</b> en cada uno: el que
                ya está, el de la planilla, o cargar los dos. Ninguna se aplica
                sola. Lo que decidas <b>dejar como está no se te vuelve a
                ofrecer</b> la próxima vez que subas la planilla.
              </p>
              {(analisis.correcciones || []).map((c) => {
                const op = eleccionDe(c.clave);
                return (
                  <div key={c.clave} className="planilla-correccion">
                    <div className="planilla-correccion__tit">
                      Tractor <b>{vacio(c.fila?.tractor)}</b> · salida{" "}
                      {vacio(c.fila?.fecha_salida)} desde{" "}
                      <b>{vacio(c.fila?.origen)}</b> · {vacio(c.fila?.chofer)}
                    </div>
                    {/* Por qué se empató con ese viaje, cuando el bot lo dice
                        (Aldo, 06/10: el mismo bitren con otro chofer y otro
                        tractor entraba como viaje nuevo). */}
                    {c.motivo && (
                      <p className="tk-meta planilla-correccion__motivo">{c.motivo}</p>
                    )}
                    <div className="tabla-wrap">
                      <table className="tabla">
                        <thead>
                          <tr>
                            <th>Campo</th>
                            <th>En el sistema (lo cargado)</th>
                            <th>En la planilla (el archivo)</th>
                          </tr>
                        </thead>
                        <tbody>
                          {Object.entries(c.cambios || {}).map(([campo, par]) => (
                            <tr key={campo}>
                              <td>{CAMPOS[campo] || campo}</td>
                              <td className={op === "reemplazar" ? "tenue" : ""}>
                                {vacio(par[0])}
                              </td>
                              <td className={op === "dejar" ? "tenue" : ""}>
                                <b>{vacio(par[1])}</b>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                    {/* La elección explícita (pedido de Aldo): con cuál me
                        quedo. Arranca en «dejar» — no tocar nada es lo seguro. */}
                    <div className="planilla-correccion__elegir" role="radiogroup"
                      aria-label={`Qué hacer con el viaje del ${tituloDe(c.fila)}`}>
                      <label>
                        <input type="radio" name={`c-${c.clave}`}
                          checked={op === "dejar"}
                          onChange={() => elegir(c.clave, "dejar")} />{" "}
                        Dejar el que está
                      </label>
                      <label>
                        <input type="radio" name={`c-${c.clave}`}
                          checked={op === "reemplazar"}
                          onChange={() => elegir(c.clave, "reemplazar")} />{" "}
                        Reemplazar por el de la planilla
                      </label>
                      <label>
                        <input type="radio" name={`c-${c.clave}`}
                          checked={op === "aparte"}
                          onChange={() => elegir(c.clave, "aparte")} />{" "}
                        Es otro viaje — cargarlo aparte
                      </label>
                    </div>
                  </div>
                );
              })}
            </>
          )}

          {(analisis.finalizados || []).length > 0 && (
            <>
              <h3>Difieren, pero el viaje está finalizado</h3>
              {/* Sin tilde a propósito (lo de Aldo, 01/09): confirmarlas
                  rebotaba contra la pared de P-91 y el clic era un callejón
                  sin salida. La diferencia se muestra igual; el camino para
                  corregirla es reabrir el viaje. */}
              <p className="tk-meta">
                Estos viajes ya se dieron por terminados (los cerró la
                llegada) y <b>no se pisan desde acá</b>. Si la diferencia es una
                corrección del mismo viaje, andá a Movimientos, tocá{" "}
                <b>Reabrir</b> y corregilo ahí — queda registrado quién lo hizo.
                Si en cambio es <b>otro viaje distinto</b>, cargalo aparte con la
                opción de abajo.
              </p>
              {(analisis.finalizados || []).map((c) => {
                const op = eleccionDe(c.clave);
                return (
                  <div key={c.clave} className="planilla-correccion">
                    <div className="planilla-correccion__tit">
                      ✓ Tractor <b>{vacio(c.fila?.tractor)}</b> · salida{" "}
                      {vacio(c.fila?.fecha_salida)} desde{" "}
                      <b>{vacio(c.fila?.origen)}</b> · {vacio(c.fila?.chofer)}{" "}
                      <span className="tenue">(finalizado)</span>
                    </div>
                    <div className="tabla-wrap">
                      <table className="tabla">
                        <thead>
                          <tr>
                            <th>Campo</th>
                            <th>En el sistema (lo cargado)</th>
                            <th>En la planilla (el archivo)</th>
                          </tr>
                        </thead>
                        <tbody>
                          {Object.entries(c.cambios || {}).map(([campo, par]) => (
                            <tr key={campo}>
                              <td>{CAMPOS[campo] || campo}</td>
                              <td className="tenue">{vacio(par[0])}</td>
                              <td>
                                <b>{vacio(par[1])}</b>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                    {/* Reemplazar NO se ofrece (P-91), pero «es otro viaje» sí:
                        cargar uno nuevo nunca toca el finalizado. */}
                    <div className="planilla-correccion__elegir" role="radiogroup"
                      aria-label={`Qué hacer con el viaje finalizado del ${tituloDe(c.fila)}`}>
                      <label>
                        <input type="radio" name={`f-${c.clave}`}
                          checked={op === "dejar"}
                          onChange={() => elegir(c.clave, "dejar")} />{" "}
                        Dejar el que está
                      </label>
                      <label>
                        <input type="radio" name={`f-${c.clave}`}
                          checked={op === "aparte"}
                          onChange={() => elegir(c.clave, "aparte")} />{" "}
                        Es otro viaje — cargarlo aparte
                      </label>
                    </div>
                  </div>
                );
              })}
            </>
          )}

          <button
            type="button"
            className="tk-boton tk-boton--fuerte"
            onClick={confirmar}
            disabled={cargando || cuantas === 0}
          >
            {cargando
              ? "Cargando…"
              : cuantas === 0
                ? "No hay nada elegido"
                : `Cargar ${cuantas}`}
          </button>
        </>
      )}
    </div>
  );
};

export default SubirPlanilla;
